import jsPDF from 'jspdf';
import 'jspdf-autotable';
import React, { useState, useEffect, useRef } from 'react';
import { testPdf } from '../utils/testPdf';
import { motion } from 'framer-motion';
import { 
  FileText, 
  Users, 
  CreditCard, 
  Heart, 
  Download
} from 'lucide-react';
import html2canvas from 'html2canvas';

// Fecha corta en español
function fechaCorta(fecha) {
  if (!fecha) return '-';
  const date = new Date(fecha);
  return date.toLocaleDateString('es-ES', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

// Traducción de estados
function traducirEstado(estado) {
  switch (estado) {
    case 'completed': return 'Completado';
    case 'pending': return 'Pendiente';
    case 'overdue': return 'Vencido';
    case 'active': return 'Activo';
    case 'inactive': return 'Inactivo';
    default: return estado;
  }
}

// Traducción de métodos de pago
function traducirMetodo(metodo) {
  if (!metodo) return '-';
  switch (metodo.toLowerCase()) {
    case 'cash': return 'Efectivo';
    case 'card': return 'Tarjeta';
    case 'transfer': return 'Transferencia';
    case 'paypal': return 'PayPal';
    default: return metodo;
  }
}

const Reports = () => {
  const [reportType, setReportType] = useState('members');
  const [members, setMembers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [donations, setDonations] = useState([]);
  const sociosTableRef = useRef();

  useEffect(() => {
    fetch('https://chungwhav2.tnf.cl/api/miembros-con-ultimo-pago')
      .then(res => res.json())
      .then(data => setMembers(data));

    fetch('https://chungwhav2.tnf.cl/api/pagos')
      .then(res => res.json())
      .then(data => setPayments(data));

    fetch('https://chungwhav2.tnf.cl/api/donaciones')
      .then(res => res.json())
      .then(data => setDonations(data));
  }, []);

  // Exportar PDF visual de socios (incluye título y total)
  const exportSociosTableAsPdf = async () => {
    if (!sociosTableRef.current) return;
    const canvas = await html2canvas(sociosTableRef.current, { backgroundColor: "#fff", scale: 2 });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'px',
      format: [canvas.width, canvas.height]
    });
    pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
    pdf.save('reporte_socios_visual.pdf');
  };

  // Botón superior: solo exporta PDF visual si está en socios
  const handleExportPdf = () => {
    if (reportType === 'members') {
      exportSociosTableAsPdf();
    } else {
      alert('Exportar PDF visual solo está disponible para Socios.');
    }
  };

  const renderReportContent = () => {
    if (reportType === 'members') {
      return (
        <div className="space-y-4">
          {/* Todo lo que esté dentro de este div se exporta al PDF visual */}
          <div ref={sociosTableRef} className="bg-white p-4 rounded-lg shadow">
            <h2 className="text-2xl font-bold text-blue-700 mb-2 text-center">
              Reporte de Socios
            </h2>
            <p className="text-gray-600 mb-4 text-center">
              Total de socios: <span className="font-bold">{members.length}</span>
            </p>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white rounded-lg shadow-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">ID</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Nombre</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Email</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Membresía</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {members.map(member => (
                    <tr key={member.id}>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{member.id}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{member.name}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{member.email}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{member.membershipType}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{traducirEstado(member.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <button
            onClick={exportSociosTableAsPdf}
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg shadow hover:bg-blue-700 transition"
          >
            Exportar Socios PDF (Visual)
          </button>
        </div>
      );
    } else if (reportType === 'payments') {
      const totalRevenue = payments.filter(p => p.status === 'completed').reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
      const pendingAmount = payments.filter(p => p.status === 'pending').reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
      const overdueAmount = payments.filter(p => p.status === 'overdue').reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);

      return (
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-gray-800">Reporte de Pagos de Membresía</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg shadow-sm">
              <p className="text-sm text-gray-600">Ingresos Totales</p>
              <p className="text-lg font-bold text-gray-900">${totalRevenue.toLocaleString('es-ES')}</p>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg shadow-sm">
              <p className="text-sm text-gray-600">Monto Pendiente</p>
              <p className="text-lg font-bold text-gray-900">${pendingAmount.toLocaleString('es-ES')}</p>
            </div>
            <div className="bg-red-50 p-4 rounded-lg shadow-sm">
              <p className="text-sm text-gray-600">Monto Vencido</p>
              <p className="text-lg font-bold text-gray-900">${overdueAmount.toLocaleString('es-ES')}</p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white rounded-lg shadow-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">ID</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Socio</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Monto</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Estado</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Fecha de Pago</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {payments.map(payment => (
                  <tr key={payment.id}>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{payment.id}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{payment.memberName}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">${parseFloat(payment.amount).toLocaleString('es-ES')}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{traducirEstado(payment.status)}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{fechaCorta(payment.paymentDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    } else if (reportType === 'donations') {
      const totalDonatedAmount = donations.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);
      const donationCount = donations.length;

      return (
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-gray-800">Reporte de Donaciones</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-green-50 p-4 rounded-lg shadow-sm">
              <p className="text-sm text-gray-600">Total Donado</p>
              <p className="text-lg font-bold text-gray-900">${totalDonatedAmount.toLocaleString('es-ES')}</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg shadow-sm">
              <p className="text-sm text-gray-600">Número de Donaciones</p>
              <p className="text-lg font-bold text-gray-900">{donationCount}</p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white rounded-lg shadow-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">ID</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Donante</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Monto</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Fecha</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Método</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {donations.map(donation => (
                  <tr key={donation.id}>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{donation.id}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{donation.donorName}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">${parseFloat(donation.amount).toLocaleString('es-ES')}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{fechaCorta(donation.donationDate)}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{traducirMetodo(donation.paymentMethod)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-8">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent mb-2">
            Centro de Reportes
          </h1>
          <p className="text-gray-600">Genere y visualice reportes clave de su organización</p>
          <button onClick={testPdf}>Probar PDF</button>
        </div>
        
        <motion.button
          onClick={handleExportPdf}
          className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Download className="w-5 h-5" />
          Exportar Reporte PDF
        </motion.button>
      </motion.div>

      {/* Report Type Selection */}
      <motion.div
        className="bg-white/90 backdrop-blur-xl border border-gray-200/50 rounded-2xl p-6 shadow-xl"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.5 }}
      >
        <h2 className="text-xl font-bold text-gray-900 mb-4">Seleccionar Tipo de Reporte</h2>
        <div className="flex flex-wrap gap-4">
          <motion.button
            onClick={() => setReportType('members')}
            className={`flex items-center gap-2 px-5 py-3 rounded-xl font-medium transition-all duration-300 ${
              reportType === 'members' ? 'bg-blue-500 text-white shadow-lg' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Users className="w-5 h-5" />
            Socios
          </motion.button>
          <motion.button
            onClick={() => setReportType('payments')}
            className={`flex items-center gap-2 px-5 py-3 rounded-xl font-medium transition-all duration-300 ${
              reportType === 'payments' ? 'bg-green-500 text-white shadow-lg' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <CreditCard className="w-5 h-5" />
            Pagos de Membresía
          </motion.button>
          <motion.button
            onClick={() => setReportType('donations')}
            className={`flex items-center gap-2 px-5 py-3 rounded-xl font-medium transition-all duration-300 ${
              reportType === 'donations' ? 'bg-red-500 text-white shadow-lg' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Heart className="w-5 h-5" />
            Donaciones
          </motion.button>
        </div>
      </motion.div>

      {/* Report Content */}
      <motion.div
        className="bg-white/90 backdrop-blur-xl border border-gray-200/50 rounded-2xl p-6 shadow-xl"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.5 }}
      >
        {renderReportContent()}
      </motion.div>
    </div>
  );
};

export default Reports;